// src/components/leitner/CoLearnerPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Sparkles, Target, TrendingUp, BookOpen, AlertTriangle, Send } from 'lucide-react';
import { taskService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';

const flattenProgress = (questions) => {
  const flattened = {};

  Object.entries(questions || {}).forEach(([key, value]) => {
    if (value && typeof value.box === 'number') {
      flattened[key] = value;
      return;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (subValue && typeof subValue.box === 'number') {
          flattened[subKey] = subValue;
        }
      });
    }
  });

  return flattened;
};

const flattenQuestions = (questions) => {
  const flat = [];
  (questions || []).forEach((q) => {
    if (q.type === 'stem' && q.subQuestions?.length > 0) {
      q.subQuestions.forEach((subQ, idx) => {
        flat.push({
          ...subQ,
          id: subQ.id || `${q.id}_sub_${idx}`,
          topics: subQ.topics || q.topics || [],
          stemId: q.id
        });
      });
    } else {
      flat.push({
        ...q,
        id: q.id,
        topics: q.topics || []
      });
    }
  });
  return flat;
};

const formatShortDate = (date) => {
  if (!date) return 'Unknown';
  const d = date?.toDate?.() || new Date(date);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CoLearnerPanel = ({ isOpen, onClose, bookId, task, questions, progress, styling }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [crossTaskOverview, setCrossTaskOverview] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [aiError, setAiError] = useState('');
  const [draft, setDraft] = useState('');
  const [threadLoaded, setThreadLoaded] = useState(false);
  const lastRequestKeyRef = useRef('');
  const lastTaskIdRef = useRef(null);

  const coLearnerCrossTask = task?.coLearnerCrossTask !== false;

  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setDraft('');
      setAiError('');
      setThreadLoaded(false);
      lastRequestKeyRef.current = '';
      lastTaskIdRef.current = null;
      return;
    }

    if (task?.id && lastTaskIdRef.current !== task.id) {
      setMessages([]);
      setDraft('');
      setAiError('');
      setThreadLoaded(false);
      lastRequestKeyRef.current = '';
      lastTaskIdRef.current = task.id;
    }
  }, [isOpen, task?.id]);

  useEffect(() => {
    if (!isOpen || !user?.uid || !bookId || !task?.id) return;

    let active = true;

    const loadThread = async () => {
      try {
        const thread = await taskService.getCoLearnerThread(bookId, task.id, user.uid);
        if (!active) return;
        if (thread?.messages?.length) {
          setMessages(thread.messages);
        }
      } catch (error) {
        console.error('Failed to load coLearner thread:', error);
      } finally {
        if (active) setThreadLoaded(true);
      }
    };

    loadThread();

    return () => {
      active = false;
    };
  }, [isOpen, user?.uid, bookId, task?.id]);

  const persistConversation = async (nextMessages) => {
    if (!user?.uid || !bookId || !task?.id) return;
    const trimmed = nextMessages.slice(-40);
    try {
      await taskService.saveCoLearnerThread(bookId, task.id, user.uid, trimmed);
    } catch (error) {
      console.error('Failed to save coLearner thread:', error);
    }
  };

  const currentTaskStats = useMemo(() => {
    const flatQuestions = flattenQuestions(questions);
    const flatProgress = flattenProgress(progress?.questions);

    if (flatQuestions.length === 0) {
      return {
        total: 0,
        mastered: 0,
        learning: 0,
        newCount: 0,
        masteryPercent: 0,
        accuracyPercent: 0,
        topicGaps: []
      };
    }

    let mastered = 0;
    let learning = 0;
    let newCount = 0;
    let totalAttempts = 0;
    let totalCorrect = 0;
    const topicStats = {};

    flatQuestions.forEach((q) => {
      const qProgress = flatProgress[q.id] || { box: 1, attempts: 0, correctAttempts: 0 };
      const box = Math.min(qProgress.box || 1, 4);

      if (box >= 4) mastered += 1;
      else if (box >= 2) learning += 1;
      else newCount += 1;

      totalAttempts += qProgress.attempts || 0;
      totalCorrect += qProgress.correctAttempts || 0;

      (q.topics || []).forEach((topic) => {
        if (!topicStats[topic]) {
          topicStats[topic] = { attempts: 0, correct: 0, count: 0, boxSum: 0 };
        }
        topicStats[topic].attempts += qProgress.attempts || 0;
        topicStats[topic].correct += qProgress.correctAttempts || 0;
        topicStats[topic].count += 1;
        topicStats[topic].boxSum += box;
      });
    });

    const masteryPercent = Math.round((mastered / flatQuestions.length) * 100);
    const accuracyPercent = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;

    const topicGaps = Object.entries(topicStats)
      .map(([topic, stats]) => ({
        topic,
        accuracy: stats.attempts > 0 ? Math.round((stats.correct / stats.attempts) * 100) : null,
        avgBox: stats.count > 0 ? (stats.boxSum / stats.count).toFixed(1) : '1'
      }))
      .sort((a, b) => {
        if (a.accuracy === null && b.accuracy === null) return Number(a.avgBox) - Number(b.avgBox);
        if (a.accuracy === null) return 1;
        if (b.accuracy === null) return -1;
        return a.accuracy - b.accuracy;
      })
      .slice(0, 3);

    return {
      total: flatQuestions.length,
      mastered,
      learning,
      newCount,
      masteryPercent,
      accuracyPercent,
      topicGaps
    };
  }, [questions, progress]);

  useEffect(() => {
    if (!isOpen || !user || !coLearnerCrossTask) {
      setCrossTaskOverview(null);
      setRecentTasks([]);
      return;
    }

    let active = true;

    const loadCrossTask = async () => {
      setLoading(true);
      try {
        const tasks = await taskService.getAvailableTasks(bookId, user.email, user.uid);
        const progressList = await Promise.all(
          tasks.map(async (taskItem) => {
            const taskProgress = await taskService.getTaskProgress(bookId, taskItem.id, user.uid);
            return taskProgress ? { task: taskItem, progress: taskProgress } : null;
          })
        );

        const activeProgress = progressList.filter(Boolean);
        if (!active) return;

        const totals = activeProgress.reduce(
          (acc, entry) => {
            const flat = flattenProgress(entry.progress.questions);
            const questionsCount = Object.keys(flat).length;
            const masteredCount = Object.values(flat).filter((q) => (q.box || 1) >= 4).length;
            const totalBox = Object.values(flat).reduce((sum, q) => sum + Math.min(q.box || 1, 4), 0);
            const maxScore = questionsCount * 4;

            acc.tasks += 1;
            acc.questions += questionsCount;
            acc.mastered += masteredCount;
            acc.totalBox += totalBox;
            acc.maxBox += maxScore;
            return acc;
          },
          { tasks: 0, questions: 0, mastered: 0, totalBox: 0, maxBox: 0 }
        );

        const masteryPercent = totals.maxBox > 0 ? Math.round((totals.totalBox / totals.maxBox) * 100) : 0;
        const masteredPercent = totals.questions > 0 ? Math.round((totals.mastered / totals.questions) * 100) : 0;

        const sortedRecent = activeProgress
          .map((entry) => ({
            id: entry.task.id,
            name: entry.task.name,
            lastActive: entry.progress.lastActivityAt
          }))
          .sort((a, b) => {
            const ad = a.lastActive?.toDate?.() || new Date(a.lastActive || 0);
            const bd = b.lastActive?.toDate?.() || new Date(b.lastActive || 0);
            return bd - ad;
          })
          .slice(0, 3);

        setRecentTasks(sortedRecent);
        setCrossTaskOverview({
          tasks: totals.tasks,
          questions: totals.questions,
          masteryPercent,
          masteredPercent
        });
      } catch (error) {
        console.error('Failed to load coLearner cross-task data:', error);
        if (active) {
          setCrossTaskOverview(null);
          setRecentTasks([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCrossTask();

    return () => {
      active = false;
    };
  }, [isOpen, user, bookId, coLearnerCrossTask]);

  useEffect(() => {
    if (!isOpen || !user || !task?.id) return;
    if (task?.coLearnerEnabled === false) return;

    const requestKey = JSON.stringify({
      taskId: task.id,
      currentTaskStats,
      crossTaskOverview: coLearnerCrossTask ? crossTaskOverview : null,
      recentTasks: coLearnerCrossTask ? recentTasks : []
    });

    if (lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;

    const generate = async () => {
      setAiLoading(true);
      setAiError('');
      try {
        const generateCoLearner = httpsCallable(functions, 'generateCoLearnerMessage');
        const response = await generateCoLearner({
          bookId,
          taskId: task.id,
          currentTaskStats,
          crossTaskOverview: coLearnerCrossTask ? crossTaskOverview : null,
          recentTasks: coLearnerCrossTask ? recentTasks : [],
          conversation: messages.slice(-6)
        });
        const nextMessage = response?.data?.message || '';
        if (nextMessage) {
          setMessages((prev) => {
            const next = [...prev, { role: 'coLearner', content: nextMessage }];
            persistConversation(next);
            return next;
          });
        }
      } catch (error) {
        console.error('Failed to generate coLearner message:', error);
        setAiError('Unable to generate coLearner message right now.');
      } finally {
        setAiLoading(false);
      }
    };

    if (threadLoaded && messages.length === 0) generate();
  }, [isOpen, user, task, bookId, currentTaskStats, crossTaskOverview, recentTasks, coLearnerCrossTask, messages.length, threadLoaded]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || aiLoading) return;
    setDraft('');
    setAiError('');
    const userTurn = { role: 'user', content: text };
    const conversationForModel = [...messages.slice(-5), userTurn];
    setMessages((prev) => {
      const next = [...prev, userTurn];
      persistConversation(next);
      return next;
    });

    setAiLoading(true);
    try {
      const generateCoLearner = httpsCallable(functions, 'generateCoLearnerMessage');
      const response = await generateCoLearner({
        bookId,
        taskId: task.id,
        currentTaskStats,
        crossTaskOverview: coLearnerCrossTask ? crossTaskOverview : null,
        recentTasks: coLearnerCrossTask ? recentTasks : [],
        userMessage: text,
        conversation: conversationForModel
      });
      const nextMessage = response?.data?.message || '';
      if (nextMessage) {
        setMessages((prev) => {
          const next = [...prev, { role: 'coLearner', content: nextMessage }];
          persistConversation(next);
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to generate coLearner message:', error);
      setAiError('Unable to generate coLearner message right now.');
    } finally {
      setAiLoading(false);
    }
  };

  const insights = useMemo(() => {
    const messages = [];

    if (currentTaskStats.total === 0) {
      messages.push("No questions loaded yet. Once we start, I'll track our rhythm together.");
      return messages;
    }

    if (currentTaskStats.masteryPercent < 40) {
      messages.push("This task still feels new. Let's build momentum with steady box-1 and box-2 wins.");
    } else if (currentTaskStats.masteryPercent < 70) {
      messages.push("We're in the learning zone. A couple focused passes should lift mastery quickly.");
    } else {
      messages.push("We're getting confident here. Want to push for deeper recall and speed?");
    }

    if (currentTaskStats.accuracyPercent > 0) {
      messages.push(`Our current accuracy is ${currentTaskStats.accuracyPercent}%. Let's nudge it upward together.`);
    }

    if (currentTaskStats.topicGaps.length > 0) {
      const gapList = currentTaskStats.topicGaps.map((gap) => gap.topic).join(', ');
      messages.push(`Trickiest topics right now: ${gapList}. We can circle back to those.`);
    }

    return messages;
  }, [currentTaskStats]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-slate-900 border-l border-slate-700 shadow-2xl z-50 flex flex-col">
      <div className="p-4 border-b border-slate-700 flex items-center justify-between bg-slate-900/95">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-500/20 rounded-lg">
            <Sparkles className="h-5 w-5 text-indigo-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Co-Learner Agent</h3>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Active & Syncing</span>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors group">
          <X className="h-5 w-5 text-slate-400 group-hover:text-white" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4">
          <p className="text-sm text-indigo-200 leading-relaxed">
            I'm your <strong>Co-Learner Agent</strong>. I'm here to study with you, track your mastery in real-time, and offer insights to help you conquer this task.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {messages.length > 0 ? (
            messages.map((msg, index) => (
              <div
                key={`${msg.role}-${index}`}
                className={`max-w-[85%] text-sm rounded-2xl px-4 py-3 ${msg.role === 'user'
                  ? 'bg-indigo-600 text-white self-end rounded-tr-none'
                  : 'bg-slate-800 text-slate-100 border border-slate-700 self-start rounded-tl-none'
                  }`}
              >
                {msg.content}
              </div>
            ))
          ) : (
            !aiLoading && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-700">
                  <BookOpen className="h-6 w-6 text-slate-500" />
                </div>
                <p className="text-sm text-slate-500">
                  {aiError || 'No messages yet. Ask me anything about the material!'}
                </p>
              </div>
            )
          )}
          {aiLoading && (
            <div className="bg-slate-800 text-slate-100 border border-slate-700 self-start rounded-2xl rounded-tl-none px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" />
              </div>
            </div>
          )}
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-3 border border-slate-700 flex items-center gap-2 group focus-within:border-indigo-500/50 transition-all">
          <input
            className="flex-1 bg-transparent px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
            placeholder="Type a message..."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSend();
            }}
            disabled={aiLoading}
          />
          <button
            onClick={handleSend}
            disabled={aiLoading || !draft.trim()}
            className="p-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Send size={18} />
          </button>
        </div>
        {aiError && <div className="text-xs text-rose-400 px-2">{aiError}</div>}

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total', value: currentTaskStats.total, icon: BookOpen },
            { label: 'Learning', value: currentTaskStats.learning, icon: Target },
            { label: 'Mastered', value: currentTaskStats.mastered, icon: TrendingUp }
          ].map((stat) => (
            <div key={stat.label} className="bg-slate-800 rounded-lg p-3 text-center border border-slate-700">
              <stat.icon className="h-4 w-4 text-slate-400 mx-auto mb-1" />
              <div className="text-lg font-semibold text-white">{stat.value}</div>
              <div className="text-xs text-slate-400">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Task Mastery</span>
            <span>{currentTaskStats.masteryPercent}%</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${currentTaskStats.masteryPercent}%`, backgroundColor: styling?.primaryColor || '#6366f1' }}
            />
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700 space-y-2">
          <div className="text-xs text-slate-500 uppercase tracking-wide">Insights</div>
          {insights.map((message, idx) => (
            <div key={`insight-${idx}`} className="text-sm text-slate-200">
              {message}
            </div>
          ))}
        </div>

        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Cross-task snapshot</div>
          {!coLearnerCrossTask && (
            <div className="flex items-start gap-2 text-sm text-amber-300">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span>Cross-task context is turned off for this task.</span>
            </div>
          )}
          {coLearnerCrossTask && loading && (
            <div className="text-sm text-slate-400">Loading your wider progress...</div>
          )}
          {coLearnerCrossTask && !loading && crossTaskOverview && (
            <div className="space-y-2 text-sm text-slate-200">
              <div>
                {crossTaskOverview.tasks} tasks active • {crossTaskOverview.questions} questions tracked
              </div>
              <div>
                Overall mastery {crossTaskOverview.masteryPercent}% • Fully mastered {crossTaskOverview.masteredPercent}%
              </div>
              {recentTasks.length > 0 && (
                <div className="text-xs text-slate-400">
                  Recent: {recentTasks.map((t) => `${t.name} (${formatShortDate(t.lastActive)})`).join(', ')}
                </div>
              )}
            </div>
          )}
          {coLearnerCrossTask && !loading && !crossTaskOverview && (
            <div className="text-sm text-slate-400">No cross-task progress yet. Let's start building it.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoLearnerPanel;
